// ─────────────────────────────────────────────
//  AlertContext — the live nerve-center for Emergency / Urgency alerts.
//  Mounted once inside AuthProvider so every screen shares ONE alert
//  subscription. Derives board-visual state and recipient lists from the live
//  `alerts` collection (see domain/alerts.js for the pure logic) and exposes
//  the raise / acknowledge / resolve actions.
//
//  • Emergency → broadcast to today's supervisors + AIC; drives board red+blink.
//  • Urgency   → silent, private to the room's one doctor.
//  • Clearing an emergency is reserved for the supervising doctor / AIC / admin.
// ─────────────────────────────────────────────
import React, { createContext, useContext, useMemo, useCallback, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import { useAlerts } from "../hooks/useAlerts";
import { useDailyRoster } from "../hooks/useDailyRoster";
import { useDayKey } from "../hooks/useDayKey";
import { useStaff } from "../hooks/useStaff";
import { acknowledgeAlert, resolveAlert } from "../firebase/firestore";
import {
  ALERT_TYPES,
  TARGET_TYPES,
  emergencyRoomIds as deriveEmergencyRoomIds,
  hasLiveEmergencyForRoom,
  resolveEmergencyTargets,
  breakRequestRoomIds as deriveBreakRequestRoomIds,
  hasLiveBreakRequestForRoom,
  breakRequestsForUser,
  conditionAlertsForRoom,
  urgenciesNeedingAlarm,
} from "../domain/alerts";
import { uidOf } from "../domain/channels";
import { normalizeBreakChannel } from "../domain/breaks";
import { userStaffIds } from "../domain/myRooms";
import { playEmergencyAlarm, playUrgencyAlarm } from "../audio/chime";
import { vibrateUrgency, vibrateEmergency } from "../native/urgencyAlarm";

const AlertContext = createContext(null);

export function AlertProvider({ children }) {
  const { user, realUser, isAIC, isAdmin } = useAuth();
  const facilityId = user?.activeFacilityId || null;
  const uid = user?.uid || null;
  // The REAL signed-in auth uid. In Testing Mode / "View As" the `user` is a
  // previewed person (their uid), but Firestore's `request.auth.uid` is always
  // the real account. The alerts CREATE rule requires senderId == auth.uid (no
  // admin bypass), so a SENT alert must carry the real uid or the write is
  // rejected and the emergency "starts then disappears". Visibility/targeting
  // still uses the previewed `uid` so the preview behaves like that person.
  const authUid = realUser?.uid || uid;

  const { alerts, sendNewAlert } = useAlerts(facilityId);
  const { roster } = useDailyRoster(facilityId, useDayKey());
  const { staff } = useStaff();
  // A1: roster/room ids are STAFF doc ids, but every recipient check (targets,
  // ack, clear, popup) keys on the AUTH uid. Resolve through staff.uid before
  // storing an alert, so the room's doctor and the supervisors are addressed by
  // their real auth uid — otherwise no one matches and ack/clear silently fail.
  const toUid = useCallback((id) => uidOf(staff, id) || id || null, [staff]);

  // ── Identity set (robust matching) ──
  // A stored alert's roomDoctorId/targetIds may be an AUTH uid (resolved) OR a
  // STAFF id (if the staff list wasn't loaded when the alert was raised, the
  // resolve falls back to the raw id). The signed-in / previewed user is known
  // by SEVERAL ids: their effective uid, their real auth uid, and their staff
  // doc id(s) (matched by uid / name / short code). Matching alert ids against
  // this whole set makes "am I a target?" and "may I clear?" work regardless of
  // which id form the alert stored — closing the gap where a room's supervising
  // doctor saw the emergency but got no Acknowledge/Clear button.
  const identity = useMemo(() => {
    const s = new Set();
    if (uid) s.add(uid);
    if (authUid) s.add(authUid);
    userStaffIds(user, staff).forEach((id) => s.add(id));
    if (realUser) userStaffIds(realUser, staff).forEach((id) => s.add(id));
    return s;
  }, [uid, authUid, user, realUser, staff]);
  const targetsMe = useCallback(
    (alert) => !!alert && Array.isArray(alert.targetIds) && alert.targetIds.some((t) => identity.has(t)),
    [identity]
  );

  // Derived board + recipient state. Visibility uses the identity set so a target
  // recorded by staff id still resolves to "mine".
  const emergencyRoomIds = useMemo(() => deriveEmergencyRoomIds(alerts), [alerts]);
  const isLive = (a) => a && (a.status === "sent" || a.status === "acknowledged");
  const myEmergencies = useMemo(
    () => alerts.filter((a) => a.type === ALERT_TYPES.EMERGENCY && isLive(a) && targetsMe(a)),
    [alerts, targetsMe]
  );
  // Emergencies that target me and I have NOT yet acknowledged — drives the popup.
  const myUnackEmergencies = useMemo(
    () => myEmergencies.filter((a) => !(Array.isArray(a.acknowledgedBy) && a.acknowledgedBy.some((x) => identity.has(x)))),
    [myEmergencies, identity]
  );
  const myUrgencies = useMemo(
    () => alerts.filter((a) => a.type === ALERT_TYPES.URGENCY && isLive(a) && targetsMe(a)),
    [alerts, targetsMe]
  );
  // Break requests (Prompt 9): rooms blinking yellow (board + TV) + the list a
  // viewer sees in My Rooms. `breakRequestsFor(isFree)` — a FREE provider (no
  // rooms of their own) sees ALL live requests so they can go cover; anyone else
  // sees only requests that target them (the room's supervising doctor).
  const breakRequestRoomIds = useMemo(() => deriveBreakRequestRoomIds(alerts), [alerts]);
  const breakRequestsFor = useCallback(
    (isFree = false) =>
      breakRequestsForUser(alerts, uid, { isFree }).filter((a) => isFree || targetsMe(a)),
    [alerts, uid, targetsMe]
  );

  // ── Emergency ALARM (sound + phone vibration) ──
  // When a NEW live emergency targets me, sound a few-second alarm and buzz the
  // phone with a long pattern. Fires for EVERY targeted user on ANY page (not
  // just the multi-room popup) — the people who must respond. Tracks seen ids so
  // it sounds once per emergency, and primes on the first snapshot so the
  // existing backlog never alarms. Baseline resets when the signed-in user
  // changes. (Browser autoplay still requires a prior gesture — handled in the
  // audio module; on a phone the app has invariably been tapped.)
  const seenEmergencyRef = useRef(null);
  useEffect(() => {
    seenEmergencyRef.current = null; // re-prime for the new user
  }, [authUid]);
  useEffect(() => {
    const ids = new Set(myEmergencies.map((a) => a.id));
    if (seenEmergencyRef.current === null) {
      seenEmergencyRef.current = ids; // prime — don't alarm the backlog
      return;
    }
    let hasNew = false;
    for (const id of ids) {
      if (!seenEmergencyRef.current.has(id)) { hasNew = true; break; }
    }
    seenEmergencyRef.current = ids;
    if (hasNew) {
      playEmergencyAlarm(); // ~3s urgent two-tone siren (ignores chime mute)
      // Every rostered doctor + the AIC (the emergency targets) buzzes too —
      // Capacitor Haptics in the native app, navigator.vibrate on the web.
      vibrateEmergency();
    }
  }, [myEmergencies]);

  // ── ⚡ Urgency ALARM (tone + vibration) ──
  // A NEW live urgency that targets me — the room's supervising doctor (or its
  // CRNA when someone else raised it) — chirps and buzzes the phone. The
  // SENDER never alarms: they get the toast + their own amber bar. Same
  // prime-on-first-snapshot pattern as the emergency alarm so a page reload
  // never re-rings the backlog. Vibration routes through native/urgencyAlarm
  // (Capacitor Haptics in the app, navigator.vibrate on the web) so it keeps
  // working when ATC ships as a native app.
  const seenUrgencyRef = useRef(null);
  useEffect(() => {
    seenUrgencyRef.current = null; // re-prime for the new user
  }, [authUid]);
  useEffect(() => {
    const ids = new Set(
      alerts.filter((a) => a.type === ALERT_TYPES.URGENCY && isLive(a)).map((a) => a.id)
    );
    if (seenUrgencyRef.current === null) {
      seenUrgencyRef.current = ids; // prime — don't alarm the backlog
      return;
    }
    const due = urgenciesNeedingAlarm(alerts, { identity, seenIds: seenUrgencyRef.current });
    seenUrgencyRef.current = ids;
    if (due.length) {
      playUrgencyAlarm(); // fast high two-tone chirp (ignores chime mute)
      vibrateUrgency(); // Haptics on native, navigator.vibrate on web
    }
  }, [alerts, identity]);

  // RAISE EMERGENCY — broadcast to the AIC + all rostered doctors + the room's
  // own supervising doctor (the CRNA→supervisor link). Deduped: refuses a 2nd
  // live emergency for the same room. roomId may be null for a floor-wide
  // broadcast from the board.
  const raiseEmergency = useCallback(
    async ({
      roomId = null,
      roomName = null,
      doctorId = null,
      // WHO IS IN THE ROOM, stamped at send time.
      //
      // The phone takeover (MobileEmergencyAlarm) names the doctor and the CRNA
      // so a supervisor knows who they are walking towards. Resolving those
      // names on the RECEIVING device would need the rooms + assignments +
      // staff listeners on every doctor's phone, and would answer "who is in
      // that room NOW" — which after a relief is a different person from the one
      // who raised the alarm. Stamping records the moment.
      //
      // Optional and defaulted: an older caller that does not pass them still
      // sends a valid emergency, and the takeover prints "not recorded" rather
      // than inventing a name.
      doctorName = null,
      crnaId = null,
      crnaName = null,
    } = {}) => {
      if (roomId && hasLiveEmergencyForRoom(alerts, roomId)) {
        return { ok: false, reason: "duplicate" };
      }
      // Resolve roster/room staff ids → auth uids so recipients actually match.
      const targetIds = Array.from(
        new Set(resolveEmergencyTargets(roster, doctorId).map(toUid).filter(Boolean))
      );
      const roomDoctorUid = toUid(doctorId);
      // No one to alert (roster not loaded yet, or no AIC/doctors named today).
      // Refuse rather than fire a silent emergency that reaches nobody.
      if (targetIds.length === 0) return { ok: false, reason: "no_targets" };
      await sendNewAlert({
        type: ALERT_TYPES.EMERGENCY,
        roomId,
        roomName,
        senderId: authUid, // real auth uid (passes the senderId==auth.uid rule)
        senderName: user?.name || null,
        targetType: TARGET_TYPES.EMERGENCY_BROADCAST,
        targetIds,
        // The room's supervising doctor (auth uid) — the CRNA→supervisor link;
        // ONLY this uid may clear the emergency (domain/alerts.canClearEmergency).
        roomDoctorId: roomDoctorUid,
        roomDoctorName: doctorName || null,
        crnaId: crnaId || null,
        crnaName: crnaName || null,
        message: `🚨 EMERGENCY${roomName ? ` in ${roomName}` : ""} — ${user?.name || "a team member"}`,
      });
      return { ok: true, targetCount: targetIds.length };
    },
    [alerts, roster, sendNewAlert, uid, authUid, user, toUid]
  );

  // RAISE URGENCY — silent, private to the room's ONE supervising doctor. Seen
  // only on that doctor's My Rooms card. The CRNA who raised it is not a target.
  const raiseUrgency = useCallback(
    async ({ roomId = null, roomName = null, doctorId = null, crnaId = null } = {}) => {
      if (!doctorId) return { ok: false, reason: "no_doctor" };
      const doctorUid = toUid(doctorId); // staff id → auth uid (A1)
      if (!doctorUid) return { ok: false, reason: "no_doctor" };
      const crnaUid = toUid(crnaId);
      // Urgency is a 1:1 room relationship: BOTH the room's supervising doctor
      // AND its provider see THEIR room blink in My Rooms (the provider's own and
      // the supervisor's). Only the supervisor clears it (roomDoctorId).
      const targetIds = Array.from(new Set([doctorUid, crnaUid].filter(Boolean)));
      await sendNewAlert({
        type: ALERT_TYPES.URGENCY,
        roomId,
        roomName,
        senderId: authUid, // real auth uid (passes the senderId==auth.uid rule)
        senderName: user?.name || null,
        targetType: TARGET_TYPES.ROOM_DOCTOR,
        targetIds,
        roomDoctorId: doctorUid,
        roomCrnaId: crnaUid || null,
        message: `⚡ ${user?.name || "A provider"} needs you${roomName ? ` in ${roomName}` : ""}`,
      });
      return { ok: true };
    },
    [sendNewAlert, uid, authUid, user, toUid]
  );

  // RAISE BREAK REQUEST (Prompt 9) — a provider in a room asks for coverage.
  // Broadcast (yellow blink) to every board + TV; targets the room's supervising
  // doctor so it also lands in their My Rooms. Deduped per room. `giver*` is the
  // person going on break (shown on the break record when someone covers).
  const raiseBreakRequest = useCallback(
    async ({ roomId = null, roomName = null, doctorId = null, giverId = null, giverName = null, breakType = null } = {}) => {
      if (!roomId) return { ok: false, reason: "no_room" };
      if (hasLiveBreakRequestForRoom(alerts, roomId)) return { ok: false, reason: "duplicate" };
      const doctorUid = toUid(doctorId);
      // Persist the CANONICAL channel on the alert — it's what the board card
      // reads when someone covers, so the requested type IS the shown type.
      // (The old code dropped breakType entirely; every cover then fell back
      // to "lunch" no matter what was asked for — the mismatch bug.)
      const channel = normalizeBreakChannel(breakType);
      const channelLabel = channel ? channel.charAt(0).toUpperCase() + channel.slice(1) : "Break";
      await sendNewAlert({
        type: ALERT_TYPES.BREAK_REQUEST,
        roomId,
        roomName,
        senderId: authUid,
        senderName: user?.name || null,
        targetType: TARGET_TYPES.BREAK_BROADCAST,
        targetIds: Array.from(new Set([doctorUid].filter(Boolean))),
        roomDoctorId: doctorUid || null,
        giverId: giverId || null,
        giverName: giverName || user?.name || null,
        breakType: channel,
        message: `☕ ${channelLabel} needed${roomName ? ` in ${roomName}` : ""} — ${giverName || user?.name || "a provider"}`,
      });
      return { ok: true };
    },
    [alerts, sendNewAlert, authUid, user, toUid]
  );

  // Resolve the live break request for a room (someone took over, or the break
  // ended) so the yellow blink clears everywhere.
  const resolveBreakRequestForRoom = useCallback(
    async (roomId) => {
      const live = alerts.filter(
        (a) => a.type === ALERT_TYPES.BREAK_REQUEST && isLive(a) && a.roomId === roomId
      );
      await Promise.all(live.map((a) => resolveAlert(a.id, uid)));
      return live.length;
    },
    [alerts, uid]
  );

  // ── Room-condition notifications (Phase 5.2) ──
  // QUIET ping to the room's supervising doctor when a CRNA posts a condition
  // or a handoff event fires (follow-up set / arrived). No alarm, no blink —
  // it feeds the "Room notifications" strip in My Rooms.
  const myConditionAlerts = useMemo(
    () => alerts.filter((a) => a.type === ALERT_TYPES.ROOM_CONDITION && isLive(a) && targetsMe(a)),
    [alerts, targetsMe]
  );
  const raiseRoomCondition = useCallback(
    async ({ roomId = null, roomName = null, doctorId = null, kind = null, message = null, extraTargetIds = [] } = {}) => {
      const doctorUid = toUid(doctorId);
      if (!doctorUid) return { ok: false, reason: "no_doctor" };
      // Phase 5.4: facility rules may escalate (e.g. notify the day's AIC too).
      const targets = Array.from(
        new Set([doctorUid, ...(extraTargetIds || []).map(toUid).filter(Boolean)])
      );
      await sendNewAlert({
        type: ALERT_TYPES.ROOM_CONDITION,
        roomId,
        roomName,
        senderId: authUid, // real auth uid (passes the senderId==auth.uid rule)
        senderName: user?.name || null,
        targetType: TARGET_TYPES.ROOM_DOCTOR,
        targetIds: targets,
        roomDoctorId: doctorUid,
        conditionKind: kind || null,
        message: message || `🩺 Room update${roomName ? ` in ${roomName}` : ""} — ${user?.name || "a provider"}`,
      });
      return { ok: true };
    },
    [sendNewAlert, authUid, user, toUid]
  );
  // Resolve every live condition ping for a room (the condition was cleared).
  const resolveConditionAlertsForRoom = useCallback(
    async (roomId) => {
      const live = conditionAlertsForRoom(alerts, roomId);
      await Promise.all(live.map((a) => resolveAlert(a.id, uid)));
      return live.length;
    },
    [alerts, uid]
  );

  // The live urgency THIS user may see for a given room (or null). Used by the
  // My Rooms room cards (board My Rooms tab + ProviderPage) — never the Full
  // Board tab or TV (BoardPage passes null there).
  const urgencyForRoom = useCallback(
    (roomId) => myUrgencies.find((a) => a.roomId === roomId) || null,
    [myUrgencies]
  );

  const acknowledge = useCallback((alertId) => acknowledgeAlert(alertId, uid), [uid]);
  const resolve = useCallback((alertId) => resolveAlert(alertId, uid), [uid]);
  // Clear (resolve) = ONLY the room's supervising doctor (matched by the identity
  // set, so a roomDoctorId stored as a staff id still resolves to "me"); admin is
  // a failsafe; the AIC may clear a floor-wide alert that has no room doctor.
  const canClear = useCallback(
    (alert) => {
      if (isAdmin) return true;
      if (!alert) return false;
      if (alert.roomDoctorId) return identity.has(alert.roomDoctorId);
      return !!isAIC;
    },
    [isAdmin, isAIC, identity]
  );

  const value = useMemo(
    () => ({
      uid,
      // THE VIEWER'S WHOLE ID SET — auth uid, effective uid, and every staff doc
      // id resolved for this person. Computed since the identity work above but
      // never published, so consumers outside this file could only ever compare
      // against the auth uid.
      //
      // That is not a cosmetic gap. A room's `relieverId` holds an AUTH uid when
      // `coverBreak` wrote it and a STAFF id when a roster picker did, so a
      // consumer matching on the uid alone hands NO break control to the person
      // actually standing in the room whenever the break was assigned from a
      // picker — which is most of them. Publishing the set lets every consumer
      // match identity the one way this context already matches alert targets.
      identityIds: identity,
      // THE STAFF LIST THIS CONTEXT ALREADY SUBSCRIBES TO.
      //
      // ⚠️ PUBLISHED SO NOBODY ELSE OPENS A SECOND LISTENER. `useStaff` has NO
      // module cache: every call registers its own auth observer AND its own
      // onSnapshot over the WHOLE staff collection. `useRoomBreakActions` is
      // mounted once per room card — fourteen rooms on a real board, and twice
      // per row on the phone's break board — so resolving a staff id to an auth
      // uid inside that hook with its own `useStaff()` would have opened a
      // dozen-plus duplicate whole-collection listeners. TimelineChartPage
      // already names this exact trap and sources its pool from a hook that was
      // subscribing anyway; this is the same move for every other consumer.
      staff,
      // THE FACILITY THESE ALERTS ARE FOR. Published so a consumer can tell.
      //
      // The scope is derived from the signed-in account (line 41) and is not
      // overridable, but until now it was also INVISIBLE: a consumer received
      // `alerts: []` and had no way to learn whether that meant "this campus is
      // quiet" or "you are looking at a different campus". A page with its own
      // facility switcher (`TimelineChartPage`) would then render a confident,
      // uniformly calm board for a campus where an emergency may be live.
      //
      // Publishing it does not change any behaviour here. It makes the mismatch
      // DETECTABLE, so a consumer can refuse to draw rather than draw a lie.
      // Compare with `alertsCoverFacility()` below rather than by hand.
      alertFacilityId: facilityId,
      alerts,
      emergencyRoomIds,
      myEmergencies,
      myUnackEmergencies,
      myUrgencies,
      raiseEmergency,
      raiseUrgency,
      urgencyForRoom,
      breakRequestRoomIds,
      breakRequestsFor,
      raiseBreakRequest,
      resolveBreakRequestForRoom,
      myConditionAlerts,
      raiseRoomCondition,
      resolveConditionAlertsForRoom,
      acknowledge,
      resolve,
      canClear,
      targetsMe,
    }),
    [uid, identity, staff, facilityId, alerts, emergencyRoomIds, myEmergencies, myUnackEmergencies, myUrgencies, raiseEmergency, raiseUrgency, urgencyForRoom, breakRequestRoomIds, breakRequestsFor, raiseBreakRequest, resolveBreakRequestForRoom, myConditionAlerts, raiseRoomCondition, resolveConditionAlertsForRoom, acknowledge, resolve, canClear, targetsMe]
  );

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
}

// Safe to call outside the provider (returns inert defaults) so a component
// never crashes if it renders before AlertProvider mounts.
const INERT = {
  uid: null,
  // `undefined`, deliberately — NOT null. No provider is mounted, so these
  // alerts cover NO facility, and `undefined` can never `===` a real facility
  // id. `alertsCoverFacility()` therefore fails CLOSED here, which is what we
  // want: an unmounted provider must not read as "that campus is quiet".
  // `null` is reserved for "provider mounted, account has no active facility".
  alertFacilityId: undefined,
  // ⚠️ PRESENT AND EMPTY, so a consumer resolving staff ids gets the right SHAPE
  // rather than `undefined`. It still resolves nobody — but a caller that reads
  // "no app account" from this is really being told "no AlertProvider", so keep
  // that in mind when a whole board reports everybody unreachable at once.
  staff: [],
  alerts: [],
  emergencyRoomIds: new Set(),
  myEmergencies: [],
  myUnackEmergencies: [],
  myUrgencies: [],
  raiseEmergency: async () => ({ ok: false, reason: "no_provider" }),
  raiseUrgency: async () => ({ ok: false, reason: "no_provider" }),
  urgencyForRoom: () => null,
  breakRequestRoomIds: new Set(),
  breakRequestsFor: () => [],
  raiseBreakRequest: async () => ({ ok: false, reason: "no_provider" }),
  resolveBreakRequestForRoom: async () => 0,
  myConditionAlerts: [],
  raiseRoomCondition: async () => ({ ok: false, reason: "no_provider" }),
  resolveConditionAlertsForRoom: async () => 0,
  acknowledge: async () => {},
  resolve: async () => {},
  canClear: () => false,
  targetsMe: () => false,
};

export function useAlertCenter() {
  return useContext(AlertContext) || INERT;
}

// Does the live alert bus actually cover `facilityId`?
//
// The one question every facility-selecting surface must ask before it draws a
// count, a lane colour, or an "all clear". The alert scope follows the signed-in
// ACCOUNT, so a page that lets the user browse another campus gets alerts for
// the wrong one — and the failure is silent in BOTH directions at once:
//
//   • `laneAlarmState` tests foreign room ids against local lanes, every lookup
//     misses, every lane returns `top: null`. The board renders confident,
//     fully-populated, and uniformly CALM for a campus that may have a live
//     emergency in it.
//   • `breakWaitingRows` falls back to the alert's own `roomName`, so a count
//     comes back non-zero next to a room name from the OTHER campus.
//
// A crash sends the reader to the telephone. A blank screen sends them to the
// telephone. A calm board sends them back to their coffee. So this fails CLOSED:
// anything falsy on either side is "not covered", never "probably fine".
//
// Callers that get `false` must render an explicit refusal. Not `0`, not blank,
// and not an all-clear empty state — "nothing outstanding" is an assertion of
// safety, and here it would be a lie.
export function alertsCoverFacility(alertFacilityId, facilityId) {
  if (!alertFacilityId || !facilityId) return false;
  return alertFacilityId === facilityId;
}
